import { useState } from "react";
import { Settings, Sparkles, SquarePen, Trash2 } from "lucide-react";
import { displayHost, sessionDisplayTitle, type SessionSummary } from "@timibot/core";
import { useApp } from "../../context";
import { useObserve } from "../../state/observable";
import { CircleIconButton, Dialog, Menu, PillSwitcher, SearchField, StatusDot, useLongPress, useMenu } from "../../ui/components";
import { TimiBadge } from "../../ui/Mascot";
import "./sidebar.css";

/** 左侧：历史对话、功能入口（技能/连接、设置）。电脑上常驻，并带“对话 / 工作台”切换。 */
export function Sidebar({ showTabs = false }: { showTabs?: boolean }) {
  const app = useApp();
  const sessions = app.sessions;
  useObserve(sessions);
  const [pendingDelete, setPendingDelete] = useState<SessionSummary>();
  const menu = useMenu();
  const [menuTarget, setMenuTarget] = useState<SessionSummary>();

  const activeID = app.selectedTab === "chat" ? app.activeChat.storedSessionID : undefined;

  return (
    <div className="sidebar">
      <div className="sidebar-head">
        <TimiBadge size={30} />
        <span className="t-headline">TimiBot</span>
        <span className="spacer" />
        <CircleIconButton icon={<SquarePen size={16} strokeWidth={2.2} />} label="新对话" onClick={() => app.newChat()} />
      </div>
      {showTabs && (
        <div className="sidebar-tabs">
          <PillSwitcher options={[["chat", "对话"], ["workspace", "工作台"]]} value={app.selectedTab} onChange={(t) => app.setTab(t)} />
        </div>
      )}
      <div className="sidebar-search">
        <SearchField placeholder="搜索对话" value={sessions.searchText} onChange={(v) => sessions.setSearch(v)} />
      </div>
      <div className="sidebar-list scroll">
        {sessions.sessions.length === 0 && !sessions.isLoading && (
          <div className="sidebar-empty t-footnote t-2">{app.connection.kind === "connected" ? "还没有对话" : "连接后显示历史对话"}</div>
        )}
        {sessions.grouped.map((group) => (
          <section key={group.title}>
            <div className="sidebar-group t-caption t-3 medium">{group.title}</div>
            {group.items.map((s) => (
              <SessionRow
                key={s.id}
                summary={s}
                active={activeID === s.id}
                onOpen={() => app.open(s)}
                onMenu={(pos) => { setMenuTarget(s); menu.show(pos); }}
              />
            ))}
          </section>
        ))}
      </div>
      <div className="sidebar-foot">
        <div className="sidebar-features">
          <button onClick={() => app.present("skills")}><Sparkles size={17} /> 技能/连接</button>
          <button onClick={() => app.present("settings")}><Settings size={17} /> 设置</button>
        </div>
        <div className="sidebar-status t-caption2 t-3">
          <StatusDot state={app.connection} />
          <span className="nowrap">{displayHost(app.server?.endpoint)}</span>
          {app.serverStatus?.version && !app.isDemo && <span className="nowrap">· v{app.serverStatus.version}</span>}
        </div>
      </div>
      <Menu
        open={menu.open}
        anchor={menu.anchor}
        onClose={menu.close}
        align="start"
        width={180}
        items={[{ label: "删除", icon: <Trash2 size={16} />, destructive: true, onSelect: () => setPendingDelete(menuTarget) }]}
      />
      <Dialog
        open={pendingDelete !== undefined}
        title="删除这个对话？"
        message="会同时删除服务器上的记录，无法恢复。"
        onClose={() => setPendingDelete(undefined)}
        buttons={[
          { title: "取消", role: "cancel" },
          { title: "删除", role: "destructive", onClick: () => { if (pendingDelete) void app.deleteSession(pendingDelete); } },
        ]}
      />
    </div>
  );
}

function SessionRow({ summary, active, onOpen, onMenu }: {
  summary: SessionSummary; active: boolean; onOpen: () => void; onMenu: (pos: { x: number; y: number }) => void;
}) {
  const press = useLongPress(onMenu);
  // 自动生成的标题有时带着 Markdown 标题符号（“## 第一部分”），去掉
  const raw = sessionDisplayTitle(summary);
  const title = raw.replace(/^[#*\s]+/, "") || raw;
  return (
    <button className={`session-row${active ? " active" : ""}`} onClick={onOpen} {...press}>
      <span className="nowrap">{title}</span>
    </button>
  );
}
